import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { Lock, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";

export function PeriodLockSettings() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const [newDate, setNewDate] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: lockInfo } = useQuery({
    queryKey: ["/api/businesses", businessId, "period-lock"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/period-lock`);
      return res.json();
    },
    enabled: !!businessId,
  });

  const lockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/businesses/${businessId}/period-lock`, { lock_date: newDate });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "period-lock"] });
      toast({ title: "Period locked", description: data.message });
      setShowConfirm(false);
      setNewDate("");
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
      setShowConfirm(false);
    },
  });

  const currentLock = lockInfo?.lockDate ? new Date(lockInfo.lockDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Period Lock</CardTitle>
        <CardDescription>Lock accounting periods to prevent changes to past transactions and journal entries.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current status */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
          {lockInfo?.locked ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-sm font-medium">Locked through {currentLock}</div>
                <div className="text-xs text-muted-foreground">Transactions on or before this date cannot be modified</div>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <div>
                <div className="text-sm font-medium">No period lock set</div>
                <div className="text-xs text-muted-foreground">Past transactions can still be modified</div>
              </div>
            </>
          )}
        </div>

        {/* Lock form */}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label>Lock through date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} max={new Date().toISOString().split("T")[0]} />
          </div>
          <Button onClick={() => setShowConfirm(true)} disabled={!newDate} className="gap-2">
            <Lock className="h-4 w-4" /> Lock Period
          </Button>
        </div>

        {/* Confirmation */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Period Lock</DialogTitle>
              <DialogDescription>This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                All transactions on or before <strong>{newDate}</strong> will be permanently locked. You will not be able to edit, delete, or post journal entries into this period.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => lockMutation.mutate()} disabled={lockMutation.isPending} className="gap-2">
                {lockMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Lock Period
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
